import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RefreshCw, Plus, Trash2, Loader2, Rss, Info, Save, CheckCircle2, AlertTriangle } from 'lucide-react';
import { knowledgeUpdate } from '../../lib/api';
import { toast } from 'sonner';

const GREEN = '#8dd110';
const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return '—'; } };

export default function KnowledgeAutoUpdate() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { knowledgeUpdate.sources().then(setCfg).catch(() => toast.error('Admins only')); }, []);
  if (!cfg) return <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setSrc = (i, k, v) => setCfg((c) => ({ ...c, sources: c.sources.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addSrc = () => upd({ sources: [...(cfg.sources || []), { name: '', url: '', type: 'rss' }] });
  const delSrc = (i) => upd({ sources: cfg.sources.filter((_, idx) => idx !== i) });
  const save = async () => {
    setSaving(true);
    try { const c = await knowledgeUpdate.saveSources(cfg); setCfg(c); toast.success('Auto-update settings saved'); }
    catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const run = async () => {
    setRunning(true); setResult(null);
    try { const r = await knowledgeUpdate.runNow(); setResult(r); toast.success(`Update: ${r.added} new, ${r.updated} refreshed`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Keep the assistant current: automatically pull the latest from California pest-control regulators and industry news into the Knowledge Base.</p>

      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="auto-update-toggle" />
            <div><div className="font-bold text-slate-900">Automatic updates</div><div className="text-xs text-slate-500">Runs in the background on the schedule below.</div></div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Frequency</span>
            <Select value={cfg.frequency || 'weekly'} onValueChange={(v) => upd({ frequency: v })}>
              <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-xs text-slate-400 mt-2">Last run: {cfg.lastRunAt ? when(cfg.lastRunAt) : 'never'}</div>
      </Card>

      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 font-bold text-slate-900"><Rss className="w-4 h-4" style={{ color: GREEN }} />Sources</div>
          <Button size="sm" variant="outline" onClick={addSrc} className="gap-1.5"><Plus className="w-4 h-4" />Add source</Button>
        </div>
        <div className="space-y-2">
          {(cfg.sources || []).map((s, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center">
              <Input value={s.name} onChange={(e) => setSrc(i, 'name', e.target.value)} placeholder="Name (e.g. CA SPCB)" className="h-9 w-44" />
              <Input value={s.url} onChange={(e) => setSrc(i, 'url', e.target.value)} placeholder="https://…  (RSS feed or page URL)" className="h-9 flex-1 min-w-[220px]" />
              <Select value={s.type || 'rss'} onValueChange={(v) => setSrc(i, 'type', v)}>
                <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="rss">RSS feed</SelectItem><SelectItem value="html">Web page</SelectItem></SelectContent>
              </Select>
              <Button size="sm" variant="ghost" onClick={() => delSrc(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
          {(cfg.sources || []).length === 0 && <p className="text-sm text-slate-400">No sources yet — add a regulator or news feed.</p>}
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={save} disabled={saving} className="gap-1.5 text-white" style={{ backgroundColor: GREEN }}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save</Button>
          <Button onClick={run} disabled={running} variant="outline" className="gap-1.5">{running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Update now</Button>
        </div>
      </Card>

      {result && (
        <Card className="p-4 border-emerald-200 bg-emerald-50/60">
          <div className="flex items-center gap-2 font-bold text-emerald-700"><CheckCircle2 className="w-5 h-5" />Update finished</div>
          <div className="text-sm text-slate-700 mt-1">{result.added} new article(s) · {result.updated} refreshed</div>
          {result.errors?.length > 0 && (
            <div className="mt-2 text-sm text-amber-700">
              <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Some sources couldn't be read:</div>
              <ul className="list-disc pl-5 text-xs mt-1">{result.errors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
        </Card>
      )}

      <Card className="p-3 border-slate-200 bg-slate-50/60 flex items-start gap-2 text-xs text-slate-500">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>New items land in <b>Pest Library → Knowledge Base</b> under “Regulatory &amp; Industry Updates,” so the assistant learns them. <b>RSS feeds</b> work best. Full summaries need an AI key (otherwise the raw excerpt is stored). Default sources are starting points — replace them with the exact feed URLs you trust.</div>
      </Card>
    </div>
  );
}
